import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  forwardRef,
  inject,
  Injector,
  input,
  OnInit,
  output,
  runInInjectionContext,
  Signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ContractAmendments } from '@app/features/amendments/contract-amendments/contract-amendments';
import { CompanyService } from '@app/features/companies/company.service';
import { Contract } from '@app/features/contracts/contract.model';
import { CdTDocument } from '@app/features/documents/document.model';
import { SalaryScaleContractUpdate } from '@app/features/salary/salary-scale.model';
import { FormErrorService } from '@app/shared/forms/form-error.service';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { DocumentsMetadata } from './../../../documents/document-metadata.model';
import { sameSlots } from '@app/features/horaires/slots-label';
import { assignedResidencies } from '@app/features/residences/residency-label';
import { embaucheFormValues } from '../embauche-form-values';
import { AssignmentForm, AssignmentFormValue } from './assignment-form/assignment-form';
import { ContractForm, ContractFormValue } from './contract-form/contract-form';
import { DocumentsForm, DocumentsFormValue } from './documents-form/documents-form';
import { EmployeeForm, EmployeeFormValue } from './employee-form/employee-form';
import { SchedulesForm, SchedulesFormValue } from './schedules-form/schedules-form';

@Component({
  selector: 'app-embauche-tabs-form',
  host: {
    '[style.--company-color]': 'companyColor',
  },
  imports: [
    CommonModule,
    MatTabsModule,
    EmployeeForm,
    ContractForm,
    SchedulesForm,
    DocumentsForm,
    AssignmentForm,
    MatTooltipModule,
    // Les avenants s'ouvrent dans un dialogue qui montre ce formulaire : la référence attend.
    forwardRef(() => ContractAmendments),
  ],
  templateUrl: './embauche-tabs-form.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './embauche-tabs-form.scss',
})
export class EmbaucheTabsForm extends RxjsDirective implements AfterViewInit, OnInit {
  @ViewChild(EmployeeForm)
  employeeForm?: EmployeeForm;
  contactSig: Signal<EmployeeFormValue> | undefined;

  @ViewChild(ContractForm)
  contractForm?: ContractForm;
  contractSig: Signal<ContractFormValue> | undefined;

  @ViewChild(SchedulesForm)
  schedulesForm?: SchedulesForm;
  schedulesSig!: Signal<SchedulesFormValue>;

  @ViewChild(AssignmentForm)
  assignmentForm?: AssignmentForm;
  assignmentSig!: Signal<AssignmentFormValue>;

  @ViewChild(DocumentsForm)
  documentsForm?: DocumentsForm;
  documentsSig!: Signal<DocumentsFormValue>;

  initWith = input<Contract>();
  validation = input<boolean>(false);
  readOnly = input<boolean>(false);
  /** Faux pour montrer un contrat tel qu'un avenant le laisse : sans ses propres avenants. */
  showAmendments = input<boolean>(true);
  /**
   * Les horaires et l'affectation d'avant un avenant (RG-avenant-consultation) : ce qui en diffère
   * est surligné, et son onglet marqué d'un point.
   */
  previousSchedules = input<SchedulesFormValue | null>(null);
  previousAssignment = input<AssignmentFormValue | null>(null);
  documentUploaded = output<CdTDocument>();
  documentsMetadataChanged = output<DocumentsMetadata>();

  private readonly blank = embaucheFormValues();
  initialContact: EmployeeFormValue = this.blank.contact;
  initialContract: ContractFormValue = this.blank.contract;
  initialSchedules: SchedulesFormValue = this.blank.schedules;
  initialAssignment: AssignmentFormValue = this.blank.assignment;
  initialDocuments: DocumentsFormValue = this.blank.documents;
  readonly formErrorService = inject(FormErrorService);

  private breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  private companyService = inject(CompanyService);
  private injector = inject(Injector);

  valueChange = output<Partial<EmployeeFormValue>>();
  orientation: 'horizontal' | 'vertical' = 'vertical';
  active = 'top';

  constructor() {
    super();
    this.register(
      this.breakpointObserver.observe(['(min-width: 830px)']).subscribe((result) => {
        this.orientation = result.matches ? 'horizontal' : 'vertical';
      }),
    );
  }

  companyColor: string | null = null;

  schedulesChanged(): boolean {
    const previous = this.previousSchedules();
    return !!previous && !sameSlots(previous.schedules, this.initialSchedules.schedules);
  }

  assignmentChanged(): boolean {
    const previous = this.previousAssignment();
    if (!previous) {
      return false;
    }
    const before = assignedResidencies(previous.assignmentType, previous.residencies);
    const after = assignedResidencies(
      this.initialAssignment.assignmentType,
      this.initialAssignment.residencies,
    );
    return (
      previous.assignmentType !== this.initialAssignment.assignmentType ||
      before.length !== after.length ||
      before.some((label) => !after.includes(label))
    );
  }

  ngOnInit(): void {
    const values = embaucheFormValues(this.initWith());
    this.initialContact = values.contact;
    this.initialContract = values.contract;
    this.initialSchedules = values.schedules;
    this.initialAssignment = values.assignment;
    this.initialDocuments = values.documents;
  }

  ngAfterViewInit(): void {
    if (this.employeeForm) {
      this.contactSig = this.employeeForm.valueSig;
    }
    if (this.contractForm) {
      this.contractSig = this.contractForm.valueSig;
    }
    if (this.schedulesForm) {
      this.schedulesSig = this.schedulesForm.valueSig;
    }
    if (this.assignmentForm) {
      this.assignmentSig = this.assignmentForm.valueSig;
    }
    if (this.documentsForm) {
      this.documentsSig = this.documentsForm.valueSig;
    }
    if (this.contractSig) {
      this.register(
        runInInjectionContext(this.injector, () =>
          toObservable(this.contractSig!)
            .pipe(
              map((v) => v.companyId),
              distinctUntilChanged(),
            )
            .subscribe((companyId) => {
              if (!companyId) {
                this.companyColor = 'white';
                return;
              }
              try {
                this.companyColor =
                  this.companyService.findById(companyId)?.companyColor || 'white';
              } catch {
                this.companyColor = 'white';
              }
            }),
        ),
      );
    }
  }

  get lastSalaryScaleContractUpdate(): SalaryScaleContractUpdate | undefined {
    const updates = this.initWith()?.salaryScaleContractUpdates || [];
    if (updates.length === 0) return undefined;
    return updates.reduce((prev, current) =>
      new Date(prev.updatedAt) > new Date(current.updatedAt) ? prev : current,
    );
  }

  onPreviousContractSelected($event: Contract) {
    this.schedulesForm?.onPreviousContractSelected($event.schedule);
    this.assignmentForm?.onPreviousContractSelected(
      $event.assignmentType,
      $event.residencies || [],
    );
    this.documentsForm?.onPreviousContractSelected($event.documents || []);
  }
}
